import {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {Network, UserData} from "../lib/types.ts";
import {fetchNetwork} from "../helpers/async-calls.ts";
import {read_cookie} from "../lib/cookies-service.ts";
import {API} from "../lib/axios.ts";
import {setupDemoInterceptor} from "../lib/demo-interceptor.ts";
import {DemoService} from "../lib/demo-service.ts";
import {getPlaces, PlaceRef} from "../lib/reference-data.ts";
import {useQuery} from "@tanstack/react-query";
import {qk} from "../lib/query-client.ts";
import {AuthService, SessionStatus} from "../lib/auth-service.ts";
import {resolveNetworkId} from "../lib/reference-queries.ts";

/**
 * `validating` is the boot state: the app has no login screen, so on every load
 * it has to ask the API who the token belongs to before it can render anything.
 */
export type AuthStatus = 'validating' | SessionStatus;

/** `idle` = not asked yet (no session, or no user to ask for). */
export type NetworkStatus = 'idle' | 'loading' | 'ready' | 'error';

// Stable identity: consumers memoise on `places`, and a fresh [] each
// render would invalidate every one of those memos.
const EMPTY_PLACES: PlaceRef[] = [];

type AppContextType = {
    user: UserData | null;
    setUser: (user: UserData | null) => void;
    token: string;
    setToken: (token: string) => void;
    loading: boolean
    setLoading: (loading: boolean) => void
    network: Network | null;
    setNetwork: (network: Network) => void
    /**
     * Whether the one network fetch has finished, and how.
     *
     * `network === null` used to mean three different things at once — not
     * asked yet, in flight, and failed — and every consumer guessed. The
     * companies page guessed "still loading" and showed a spinner that never
     * resolved, because `fetchNetwork` swallows its error and returns null.
     * A page can now tell "no answer yet" from "asked, and it failed".
     */
    networkStatus: NetworkStatus;
    /**
     * `networks.id` — what every `network/{id}/…` route takes. Never the HQ
     * company id: they coincide on local network 1 and nowhere else that
     * matters (FRA-20261025-001). Null until `GET network` lands and if it
     * fails; callers stay disabled on null rather than request
     * `network/undefined/…`.
     */
    networkId: number | null;
    // Resolved once at boot and then only on an explicit refresh (after
    // /verify bakes a fresh token) or a 401. Routing reads this — it never
    // triggers its own `/user` call, so navigation costs no requests.
    authStatus: AuthStatus;
    refreshSession: () => Promise<AuthStatus>;
    // Network-wide places, loaded once with the network and shared by every
    // page that has to turn a `place_id` into something readable (reports,
    // custom reports, filter pickers). Reference data — never refetched, and
    // never period-scoped. Empty until it arrives, and empty if it fails:
    // callers fall back to the raw id rather than blocking on a label.
    places: PlaceRef[];
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<UserData | null>(null);
    const [network, setNetwork] = useState<Network | null>(null)
    const [networkStatus, setNetworkStatus] = useState<NetworkStatus>('idle')
    const [loading, setLoading] = useState(false)
    const [token, setToken] = useState("")
    const [authStatus, setAuthStatus] = useState<AuthStatus>('validating')

    /**
     * Network-wide places. `/places` is network-scoped for this app (api-v1
     * applies it off the `X-ZOOZA-SCOPE: downstream` header our axios always
     * sends), so this is ONE request for every page that needs a place name —
     * not one per report, per period, or per company.
     *
     * Courses have an equivalent lookup but are deliberately NOT loaded here:
     * places are needed by several pages, while a course name is needed only by
     * a report that groups by course, so that query lives where it is used
     * (`use-dimension-labels`).
     *
     * A missing label is never worth breaking the app over, so a failure yields
     * an empty list and callers fall back to the raw id.
     */
    const {data: places = EMPTY_PLACES} = useQuery({
        queryKey: qk.referenceData.places(),
        queryFn: ({signal}) => getPlaces(signal),
        enabled: !!user?.user_valid,
        staleTime: 10 * 60_000,
    });

    const refreshSession = useCallback(async (): Promise<AuthStatus> => {
        setAuthStatus('validating');

        const {status, user: sessionUser} = await AuthService.getInstance().resolveSession();

        setUser(status === 'authenticated' ? sessionUser : null);
        setAuthStatus(status);

        return status;
    }, []);

    // Setup interceptors on mount
    useEffect(() => {
        setupDemoInterceptor(API);

        const requestInterceptorId = API.interceptors.request.use(config => {
            const cookieToken = read_cookie("zooza-token");
            const company_id = read_cookie("zooza-active-company");
            if (cookieToken) {
                config.headers['X-ZOOZA-TOKEN'] = cookieToken;
            }
            if (company_id) {
                config.headers['X-ZOOZA-COMPANY'] = company_id;
            }
            return config;
        });

        // A token can die mid-session. Without this every subsequent request
        // just fails into a snackbar and the user is left on a shell of a page
        // with no idea their session is gone.
        //
        // api-v1 does not use 401 for this. A dead token gets HTTP 400, and the
        // error body carries `user_valid` — `false` means the session is over,
        // `true` means an ordinary error that happens to have failed. So the
        // flag is the signal, not the status code; keying off 400 alone would
        // log people out on any validation error. (`error_data.user` is null in
        // both cases and is no use here.) 401 is still honoured in case the API
        // ever starts sending it.
        const responseInterceptorId = API.interceptors.response.use(
            response => response,
            error => {
                const status = error?.response?.status;
                const sessionDead =
                    status === 401 || error?.response?.data?.user_valid === false;

                if (sessionDead) {
                    AuthService.getInstance().clearAuth();
                    setUser(null);
                    setToken("");
                    setAuthStatus('no_session');
                }
                return Promise.reject(error);
            }
        );

        return () => {
            API.interceptors.request.eject(requestInterceptorId);
            API.interceptors.response.eject(responseInterceptorId);
        };
    }, []);

    // One boot-time resolve. Interceptors above are registered in an earlier
    // effect, so they are already in place by the time this fires.
    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    useEffect(() => {
        const demoService = DemoService.getInstance();

        if (user?.user_valid && (token.length !== 0 || read_cookie("zooza-token")?.length !== 0 || demoService.isDemoMode())) {
            setNetworkStatus('loading');
            fetchNetwork().then((network) => {
                if (network) {
                    setNetwork(network);
                    setNetworkStatus('ready');
                } else {
                    // fetchNetwork swallows the error and returns null, so this
                    // is the only place the failure is still visible. Consumers
                    // read the status rather than inferring it from a null
                    // network they cannot distinguish from "not yet".
                    setNetworkStatus('error');
                }
            });

        }
    }, [user, token]);

    const value = {
        user,
        setUser,
        token,
        setToken,
        loading,
        setLoading,
        network,
        setNetwork,
        networkStatus,
        networkId: resolveNetworkId(network),
        places,
        authStatus,
        refreshSession
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider');
    }
    return context;
};
